import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MdInfoOutline, MdSearch } from 'react-icons/md';

const STEP_GUIDE_STEPS = [
  {
    color: '#3b82f6',
    title: 'Pick an API',
    desc: 'Click "+ Create New Test Step" and select an API from the Collection. The step inherits the URL, method, and headers from that API definition.',
  },
  {
    color: '#10b981',
    title: 'Set a Payload',
    desc: '"Specific Payload" — a fixed JSON object run once. "Test Data File" — links a CSV so the step runs once per row in Data-Driven mode. You can set both.',
  },
  {
    color: '#f59e0b',
    title: 'Add Capture Rules',
    desc: 'Extract values from the response (e.g. Store As: token, Path: $.data.accessToken). Captured values become {{token}} usable in later suite steps.',
  },
  {
    color: '#8b5cf6',
    title: 'Group Steps',
    desc: 'Use Test Groups to bundle related steps (e.g. all login variants) and reference the entire group as one item inside a Test Suite.',
  },
  {
    color: '#ec4899',
    title: 'Run Directly or via Suite',
    desc: 'Each step has a Run button for quick standalone testing. For end-to-end chained flows, add steps to a Test Suite and execute from there.',
  },
];

function TestStepsQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>Test Steps — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {STEP_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CAPTURE_KEY_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

const createEmptyCaptureRule = () => ({ storeAs: '', path: '' });

function TestSteps({ onStats, onExecutionComplete }) {
  const [testSteps, setTestSteps] = useState([]);
  const [testGroups, setTestGroups] = useState([]);
  const [apis, setApis] = useState([]);
  const [csvFiles, setCsvFiles] = useState([]);
  const [expandedStepId, setExpandedStepId] = useState(null);
  const [runModeByStep, setRunModeByStep] = useState({});
  const [payloadDraftByStep, setPayloadDraftByStep] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [executingStepId, setExecutingStepId] = useState(null);
  const [executingGroupId, setExecutingGroupId] = useState(null);
  const [savingPayloadStepId, setSavingPayloadStepId] = useState(null);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [deletingStep, setDeletingStep] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchInputRef = useRef(null);

  const [formData, setFormData] = useState({
    apiId: '',
    name: '',
    description: '',
    specificPayload: '',
    testDataId: '',
    responseCaptureRules: [],
    expectedStatus: ''
  });
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    testStepIds: []
  });

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    fetchTestSteps();
    fetchTestGroups();
    fetchApis();
    fetchCsvFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTestSteps = async () => {
    try {
      const response = await axios.get('/api/teststeps');
      const list = Array.isArray(response.data) ? response.data : [];
      setTestSteps(list);
      if (onStats) onStats();
      setExpandedStepId((prev) => (list.some((step) => step.id === prev) ? prev : null));

      setPayloadDraftByStep((prev) => {
        const next = {};
        list.forEach((step) => {
          if (Object.prototype.hasOwnProperty.call(prev, step.id)) {
            next[step.id] = prev[step.id];
          }
        });
        return next;
      });

      setGroupFormData((prev) => ({
        ...prev,
        testStepIds: prev.testStepIds.filter((testStepId) => list.some((step) => step.id === testStepId))
      }));
    } catch (fetchError) {
      setError('Failed to fetch test steps: ' + fetchError.message);
    }
  };

  const fetchTestGroups = async () => {
    try {
      const response = await axios.get('/api/testgroups');
      const list = Array.isArray(response.data) ? response.data : [];
      setTestGroups(list);
    } catch (fetchError) {
      setError('Failed to fetch test groups: ' + fetchError.message);
    }
  };

  const fetchApis = async () => {
    try {
      const response = await axios.get('/api/apis');
      setApis(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch APIs:', fetchError);
    }
  };

  const fetchCsvFiles = async () => {
    try {
      const response = await axios.get('/api/csv');
      setCsvFiles(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch CSV files:', fetchError);
    }
  };

  const hasText = (value) => Boolean(value && String(value).trim());

  const isJsonString = (value) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  const sanitizeCaptureRules = (rules) => {
    const sourceRules = Array.isArray(rules) ? rules : [];
    const normalizedRules = [];
    const seenStoreAs = new Set();

    for (let index = 0; index < sourceRules.length; index += 1) {
      const rule = sourceRules[index] || {};
      const storeAs = String(rule.storeAs || '').trim();
      const path = String(rule.path || '').trim();

      if (!storeAs && !path) {
        continue;
      }

      if (!storeAs || !path) {
        return {
          error: `Capture rule ${index + 1}: fill both "Store As" and "Response Path".`
        };
      }

      if (!CAPTURE_KEY_REGEX.test(storeAs)) {
        return {
          error: `Capture rule ${index + 1}: "${storeAs}" is invalid. Use letters, numbers, and underscore only.`
        };
      }

      const duplicateKey = storeAs.toLowerCase();
      if (seenStoreAs.has(duplicateKey)) {
        return {
          error: `Capture key "${storeAs}" is duplicated. Use unique names.`
        };
      }

      seenStoreAs.add(duplicateKey);
      normalizedRules.push({ storeAs, path });
    }

    return { value: normalizedRules };
  };

  const getStepCaptureRules = (step) => {
    if (!Array.isArray(step?.responseCaptureRules)) return [];
    return step.responseCaptureRules
      .map((rule) => ({
        storeAs: String(rule?.storeAs || '').trim(),
        path: String(rule?.path || '').trim()
      }))
      .filter((rule) => rule.storeAs && rule.path);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const hasSpecificInput = hasText(formData.specificPayload);
    const hasTestDataInput = hasText(formData.testDataId);

    if (hasSpecificInput && !isJsonString(formData.specificPayload)) {
      setError('Specific Payload must be valid JSON.');
      return;
    }

    const sanitizedRules = sanitizeCaptureRules(formData.responseCaptureRules);
    if (sanitizedRules.error) {
      setError(sanitizedRules.error);
      return;
    }

    const payload = {
      ...formData,
      specificPayload: hasSpecificInput ? formData.specificPayload : '',
      testDataId: hasTestDataInput ? formData.testDataId : '',
      responseCaptureRules: sanitizedRules.value
    };

    try {
      if (editingId) {
        await axios.put(`/api/teststeps/${editingId}`, payload);
        setSuccess('Test step updated successfully');
      } else {
        await axios.post('/api/teststeps', payload);
        setSuccess('Test step created successfully');
      }


      setFormData({
        apiId: '',
        name: '',
        description: '',
        specificPayload: '',
        testDataId: '',
        responseCaptureRules: [],
        expectedStatus: ''
      });
      fetchTestSteps();
    } catch (saveError) {
      setError('Failed to save test step: ' + (saveError.response?.data?.error || saveError.message));
    }
  };

  const handleEdit = (testStep) => {
    setFormData({
      apiId: testStep.apiId || '',
      name: testStep.name || '',
      description: testStep.description || '',
      specificPayload: testStep.specificPayload || '',
      testDataId: testStep.testDataId || '',
      responseCaptureRules: getStepCaptureRules(testStep),
      expectedStatus: testStep.expectedStatus || ''
    });
    setEditingId(testStep.id);
    setShowForm(true);
  };

  const handleAddCaptureRule = () => {
    setFormData((prev) => ({
      ...prev,
      responseCaptureRules: [...prev.responseCaptureRules, createEmptyCaptureRule()]
    }));
  };

  const handleRemoveCaptureRule = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      responseCaptureRules: prev.responseCaptureRules.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleCaptureRuleChange = (indexToUpdate, field, value) => {
    setFormData((prev) => ({
      ...prev,
      responseCaptureRules: prev.responseCaptureRules.map((rule, index) => (
        index === indexToUpdate ? { ...rule, [field]: value } : rule
      ))
    }));
  };

  const getInlinePayloadDraft = (step) => {
    if (Object.prototype.hasOwnProperty.call(payloadDraftByStep, step.id)) {
      return payloadDraftByStep[step.id];
    }
    return step.specificPayload || '';
  };

  const handleInlinePayloadDraftChange = (stepId, value) => {
    setPayloadDraftByStep((prev) => ({
      ...prev,
      [stepId]: value
    }));
  };

  const handleInlinePayloadSave = async (step) => {
    setError('');
    setSuccess('');

    const draftPayload = getInlinePayloadDraft(step);
    const hasSpecificInput = hasText(draftPayload);

    if (hasSpecificInput && !isJsonString(draftPayload)) {
      setError('Specific Payload must be valid JSON before updating.');
      return;
    }

    setSavingPayloadStepId(step.id);
    try {
      await axios.put(`/api/teststeps/${step.id}`, {
        specificPayload: hasSpecificInput ? draftPayload : ''
      });
      setSuccess(`Specific payload updated for "${step.name}"`);
      setPayloadDraftByStep((prev) => {
        const next = { ...prev };
        delete next[step.id];
        return next;
      });
      fetchTestSteps();
    } catch (saveError) {
      setError('Failed to update payload: ' + (saveError.response?.data?.error || saveError.message));
    } finally {
      setSavingPayloadStepId(null);
    }
  };

  const openDeleteModal = (step) => {
    setError('');
    setSuccess('');
    setStepToDelete(step);
  };

  const closeDeleteModal = () => {
    if (deletingStep) return;
    setStepToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!stepToDelete) return;

    setDeletingStep(true);
    setError('');

    try {
      await axios.delete(`/api/teststeps/${stepToDelete.id}`);
      setSuccess(`Test step "${stepToDelete.name}" deleted successfully`);
      setStepToDelete(null);
      await Promise.all([fetchTestSteps(), fetchTestGroups()]);
    } catch (deleteError) {
      setError('Failed to delete test step: ' + deleteError.message);
    } finally {
      setDeletingStep(false);
    }
  };

  const hasSpecificPayload = (step) => hasText(step?.specificPayload);
  const hasTestDataFile = (step) => hasText(step?.testDataId);
  const canRunStep = (step) => hasSpecificPayload(step) || hasTestDataFile(step);
  const getGroupStepIds = (group) => {
    const rawIds = Array.isArray(group?.testStepIds) ? group.testStepIds : [];
    return rawIds
      .map((rawStepId) => String(rawStepId?.testStepId || rawStepId?.id || rawStepId || '').trim())
      .filter(Boolean);
  };
  const canRunGroup = (group) => {
    return getGroupStepIds(group).some((stepId) => {
      const step = testSteps.find((item) => item.id === stepId);
      return step ? canRunStep(step) : false;
    });
  };

  const getRunMode = (step) => {
    const selectedMode = runModeByStep[step.id] || step.payloadType || 'specific';

    if (selectedMode === 'specific' && hasSpecificPayload(step)) return 'specific';
    if (selectedMode === 'testData' && hasTestDataFile(step)) return 'testData';
    if (hasSpecificPayload(step)) return 'specific';
    if (hasTestDataFile(step)) return 'testData';
    return selectedMode;
  };

  const handleRunModeChange = (stepId, mode) => {
    setRunModeByStep((prev) => ({
      ...prev,
      [stepId]: mode
    }));
  };

  const handleRunStep = async (step) => {
    setError('');
    setSuccess('');

    if (!canRunStep(step)) {
      setError(`Cannot run "${step.name}". Configure a specific payload or a test data file first.`);
      return;
    }

    const runMode = getRunMode(step);
    if (runMode === 'specific' && !hasSpecificPayload(step)) {
      setError(`Cannot run "${step.name}" with specific payload. Add specific payload in Edit first.`);
      return;
    }

    if (runMode === 'testData' && !hasTestDataFile(step)) {
      setError(`Cannot run "${step.name}" with test data. Assign a test data file in Edit first.`);
      return;
    }

    setExecutingStepId(step.id);

    try {
      const response = await axios.post(`/api/execution/teststep/${step.id}`, {
        payloadTypeOverride: runMode
      });
      if (onExecutionComplete) {
        onExecutionComplete(response.data);
      }
      setSuccess(
        `Test step executed using ${runMode === 'specific' ? 'specific payload' : 'test data file'}! ` +
        `Results: ${response.data.summary.passed}/${response.data.summary.total} passed`
      );
    } catch (runError) {
      setError('Failed to execute test step: ' + (runError.response?.data?.error || runError.message));
    } finally {
      setExecutingStepId(null);
    }
  };

  const handleRunGroup = async (group) => {
    setError('');
    setSuccess('');

    if (!canRunGroup(group)) {
      setError(`Cannot run "${group.name}". Add at least one runnable test step first.`);
      return;
    }

    setExecutingGroupId(group.id);

    try {
      const response = await axios.post(`/api/execution/testgroup/${group.id}`);
      const summary = response?.data?.summary || {};
      if (onExecutionComplete) {
        onExecutionComplete(response.data);
      }
      setSuccess(`Test group executed! Results: ${summary.passed || 0}/${summary.total || 0} passed`);
    } catch (runError) {
      setError('Failed to execute test group: ' + (runError.response?.data?.error || runError.message));
    } finally {
      setExecutingGroupId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      apiId: '',
      name: '',
      description: '',
      specificPayload: '',
      testDataId: '',
      responseCaptureRules: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getApiName = (apiId) => {
    const api = apis.find((a) => a.id === apiId);
    return api ? api.name : 'Unknown API';
  };

  const getCsvName = (csvId) => {
    const csv = csvFiles.find((c) => c.id === csvId);
    return csv ? csv.fileName : 'Unknown File';
  };

  const resetGroupForm = () => {
    setGroupFormData({
      name: '',
      description: '',
      testStepIds: []
    });
    setEditingGroupId(null);
    setShowGroupForm(false);
  };

  const handleGroupStepToggle = (testStepId) => {
    setGroupFormData((prev) => {
      const alreadySelected = prev.testStepIds.includes(testStepId);
      return {
        ...prev,
        testStepIds: alreadySelected
          ? prev.testStepIds.filter((id) => id !== testStepId)
          : [...prev.testStepIds, testStepId]
      };
    });
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const groupName = String(groupFormData.name || '').trim();
    if (!groupName) {
      setError('Test group name is required');
      return;
    }

    if (groupFormData.testStepIds.length === 0) {
      setError('Select at least one test step for the group');
      return;
    }

    const payload = {
      name: groupName,
      description: groupFormData.description || '',
      testStepIds: groupFormData.testStepIds
    };

    try {
      if (editingGroupId) {
        await axios.put(`/api/testgroups/${editingGroupId}`, payload);
        setSuccess('Test group updated successfully');
      } else {
        await axios.post('/api/testgroups', payload);
        setSuccess('Test group created successfully');
      }

      resetGroupForm();
      fetchTestGroups();
    } catch (saveError) {
      setError('Failed to save test group: ' + (saveError.response?.data?.error || saveError.message));
    }
  };

  const handleEditGroup = (group) => {
    setGroupFormData({
      name: group.name || '',
      description: group.description || '',
      testStepIds: Array.isArray(group.testStepIds) ? group.testStepIds : []
    });
    setEditingGroupId(group.id);
    setShowGroupForm(true);
  };

  const openDeleteGroupModal = (group) => {
    setError('');
    setSuccess('');
    setGroupToDelete(group);
  };

  const closeDeleteGroupModal = () => {
    if (deletingGroup) return;
    setGroupToDelete(null);
  };

  const handleConfirmDeleteGroup = async () => {
    if (!groupToDelete) return;

    setDeletingGroup(true);
    setError('');

    try {
      await axios.delete(`/api/testgroups/${groupToDelete.id}`);
      setSuccess(`Test group "${groupToDelete.name}" deleted successfully`);
      setGroupToDelete(null);
      fetchTestGroups();
    } catch (deleteError) {
      setError('Failed to delete test group: ' + (deleteError.response?.data?.error || deleteError.message));
    } finally {
      setDeletingGroup(false);
    }
  };

  const getGroupStepNames = (group) => {
    return getGroupStepIds(group).map((stepId) => {
      const step = testSteps.find((item) => item.id === stepId);
      return step ? step.name : `Missing (${stepId})`;
    });
  };

  const searchQuery = searchTerm.trim().toLowerCase();

  const filteredTestSteps = testSteps.filter((step) => {
    if (!searchQuery) return true;
    const api = apis.find((item) => item.id === step.apiId);
    const csv = csvFiles.find((item) => item.id === step.testDataId);
    const searchableText = [
      step.name || '',
      step.description || '',
      step.specificPayload || '',
      step.payloadType || '',
      step.expectedStatus || '',
      api?.name || '',
      api?.url || '',
      api?.method || '',
      csv?.fileName || '',
      Array.isArray(step.responseCaptureRules)
        ? step.responseCaptureRules.map((rule) => `${rule.storeAs || ''} ${rule.path || ''}`).join(' ')
        : ''
    ].join(' ').toLowerCase();
    return searchableText.includes(searchQuery);
  });

  const filteredTestGroups = testGroups.filter((group) => {
    if (!searchQuery) return true;
    const stepNames = getGroupStepNames(group);
    const searchableText = [
      group.name || '',
      group.description || '',
      stepNames.join(' '),
      group.createdAt ? new Date(group.createdAt).toLocaleString() : ''
    ].join(' ').toLowerCase();
    return searchableText.includes(searchQuery);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h1 style={{ margin: 0 }}>Test Steps</h1>
        {showSearch ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'min(460px, 100%)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search steps, groups, APIs, payloads, or test data"
              style={{ minWidth: '220px' }}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
              title="Close search"
            >
              x
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowSearch(true)}
            aria-label="Show search"
            title="Search"
          >
            <MdSearch />
          </button>
        )}
      </div>
      <TestStepsQuickStartGuide />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <button
        className="btn btn-primary"
        onClick={() => {
          if (showForm) {
            resetForm();
          } else {
            setShowForm(true);
          }
        }}
      >
        {showForm ? 'Cancel' : '+ Create New Test Step'}
      </button>

      {showForm && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>{editingId ? 'Edit Test Step' : 'Create New Test Step'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Choose API *</label>
              <select
                value={formData.apiId}
                onChange={(e) => setFormData({ ...formData, apiId: e.target.value })}
                required
              >
                <option value="">Select an API</option>
                {apis.map((api) => (
                  <option key={api.id} value={api.id}>
                    {api.name} ({api.method} {api.url})
                  </option>
                ))}
              </select>
              {apis.length === 0 && (
                <small style={{ color: '#ff6b6b' }}>No APIs created yet. Create one in API Collection first.</small>
              )}
            </div>

            <div className="form-group">
              <label>Test Step Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Valid Login, Invalid Password Test"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this test step"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Assign Test Data File (Optional)</label>
              <select
                value={formData.testDataId}
                onChange={(e) => setFormData({ ...formData, testDataId: e.target.value })}
              >
                <option value="">No Test Data File Linked</option>
                {csvFiles.map((csv) => (
                  <option key={csv.id} value={csv.id}>
                    {csv.fileName} ({csv.rowCount} rows)
                  </option>
                ))}
              </select>
              {csvFiles.length === 0 && (
                <small style={{ color: '#ff6b6b' }}>
                  No test data files uploaded yet. Upload one in Test Data first.
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Specific Payload JSON (Optional)</label>
              <textarea
                value={formData.specificPayload}
                onChange={(e) => setFormData({ ...formData, specificPayload: e.target.value })}
                placeholder='{"email": "test@example.com", "password": "test123", "expectedStatus": 200}'
                rows={8}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              <small>Use valid JSON if provided. You can leave this empty and configure later.</small>
            </div>

            <div className="form-group">
              <label>Capture Data From Response (Optional)</label>
              {formData.responseCaptureRules.map((rule, index) => (
                <div
                  key={index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr auto',
                    gap: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <input
                    type="text"
                    value={rule.storeAs}
                    onChange={(e) => handleCaptureRuleChange(index, 'storeAs', e.target.value)}
                    placeholder="Store As (e.g., accessToken)"
                  />
                  <input
                    type="text"
                    value={rule.path}
                    onChange={(e) => handleCaptureRuleChange(index, 'path', e.target.value)}
                    placeholder="Response Path (e.g., $.data.accessToken)"
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveCaptureRule(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddCaptureRule}
              >
                + Add Capture Rule
              </button>
              <small style={{ display: 'block', marginTop: '6px' }}>
                On successful response, values from these paths are stored and can be reused as placeholders like {'{{accessToken}}'}.
              </small>
            </div>

            <div className="form-group">
              <label>Expected Status Code</label>
              <input
                type="number"
                placeholder="e.g. 200 (Leave empty to use API default)"
                value={formData.expectedStatus}
                onChange={(e) => setFormData({ ...formData, expectedStatus: e.target.value })}
              />
              <small>Overrides the default expected status defined in the API Test.</small>
            </div>

            <button type="submit" className="btn btn-success">
              {editingId ? 'Update Test Step' : 'Create Test Step'}
            </button>
          </form>
        </div>
      )}

      <h2 style={{ marginTop: '30px' }}>Created Test Steps ({filteredTestSteps.length})</h2>
      {testSteps.length === 0 ? (
        <div className="card">
          <p>No test steps created yet. Create one to define reusable test configurations.</p>
        </div>
      ) : filteredTestSteps.length === 0 ? (
        <div className="card">
          <p>No test steps found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '34px minmax(180px, 1fr) minmax(220px, 1.2fr) minmax(170px, 1fr) minmax(170px, 0.9fr)',
                minWidth: '900px',
                gap: '10px',
                alignItems: 'center',
                padding: '0 12px',
                fontSize: '11px',
                color: '#6b7280',
                fontWeight: 700,
                textTransform: 'uppercase'
              }}
            >
              <span> </span>
              <span>Name</span>
              <span>API</span>
              <span>Time</span>
              <span>Actions</span>
            </div>
          </div>
          {filteredTestSteps.map((step) => {
            const runMode = getRunMode(step);
            const specificEnabled = hasSpecificPayload(step);
            const testDataEnabled = hasTestDataFile(step);
            const captureRules = getStepCaptureRules(step);
            const payloadDraft = getInlinePayloadDraft(step);
            const isExpanded = expandedStepId === step.id;

            return (
              <div key={step.id} className="card" style={{ borderLeft: '4px solid #4a90d9', padding: '12px 14px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '34px minmax(180px, 1fr) minmax(220px, 1.2fr) minmax(170px, 1fr) minmax(170px, 0.9fr)',
                      minWidth: '900px',
                      gap: '10px',
                      alignItems: 'center'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedStepId((prev) => (prev === step.id ? null : step.id))}
                      className="btn btn-secondary btn-icon-sq"
                      style={{
                        borderRadius: '999px',
                        backgroundColor: '#f8fbff',
                        color: '#4a90d9',
                        fontWeight: 700,
                        border: '1px solid #d8dee9',
                      }}
                      title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                      {isExpanded ? String.fromCharCode(9660) : String.fromCharCode(9654)}
                    </button>

                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {step.name}
                    </strong>
                    <span style={{ color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getApiName(step.apiId)}
                    </span>
                    <span style={{ color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {step.createdAt ? new Date(step.createdAt).toLocaleString() : '-'}
                    </span>

                    <div className="action-buttons" style={{ gap: '8px', flexWrap: 'nowrap' }}>
                      <button
                        className="btn btn-success btn-icon-sq"
                        onClick={() => handleRunStep(step)}
                        disabled={executingStepId === step.id || !canRunStep(step)}
                        title={executingStepId === step.id ? 'Running' : 'Run'}
                        aria-label={executingStepId === step.id ? 'Running' : 'Run'}
                        style={{ fontSize: '15px' }}
                      >
                        {executingStepId === step.id ? String.fromCharCode(8230) : String.fromCharCode(9654)}
                      </button>
                      <button
                        className="btn btn-primary btn-icon-sq"
                        onClick={() => handleEdit(step)}
                        title="Edit"
                        aria-label="Edit"
                        style={{ fontSize: '15px' }}
                      >
                        {String.fromCharCode(9998)}
                      </button>
                      <button
                        className="btn btn-danger btn-icon-sq"
                        onClick={() => openDeleteModal(step)}
                        title="Delete"
                        aria-label="Delete"
                        style={{ fontSize: '15px' }}
                      >
                        {String.fromCodePoint(128465)}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: '14px', borderTop: '1px solid #e9ecef', paddingTop: '14px' }}>
                    {step.description && <p style={{ margin: '0 0 10px 0', color: '#666' }}>{step.description}</p>}
                    {testDataEnabled && (
                      <p style={{ margin: '5px 0' }}>
                        <strong>Assigned Test Data:</strong> {getCsvName(step.testDataId)}
                      </p>
                    )}
                    {captureRules.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <strong style={{ fontSize: '13px' }}>Capture Response Data:</strong>
                        {captureRules.map((rule, index) => (
                          <p key={index} style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#555' }}>
                            {rule.storeAs} {String.fromCharCode(8592)} {rule.path}
                          </p>
                        ))}
                      </div>
                    )}

                    <details style={{ marginTop: '10px' }}>
                      <summary style={{ cursor: 'pointer', color: '#4a90d9' }}>
                        View / Edit Specific Payload
                      </summary>
                      <textarea
                        value={payloadDraft}
                        onChange={(e) => handleInlinePayloadDraftChange(step.id, e.target.value)}
                        rows={8}
                        placeholder='{"email": "test@example.com", "password": "test123"}'
                        style={{
                          width: '100%',
                          marginTop: '8px',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontFamily: 'monospace',
                          fontSize: '12px'
                        }}
                      />
                      <div style={{ marginTop: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleInlinePayloadSave(step)}
                          disabled={savingPayloadStepId === step.id}
                        >
                          {savingPayloadStepId === step.id ? 'Updating...' : 'Update Payload'}
                        </button>
                      </div>
                    </details>

                    <div className="action-buttons" style={{ alignItems: 'flex-start', marginTop: '14px' }}>
                      <div style={{ minWidth: '240px' }}>
                        <p style={{ margin: '0 0 6px 0', fontSize: '12px' }}>
                          <strong>Run With:</strong>
                        </p>
                        <div className="btn-toggle-group">
                          <button
                            type="button"
                            onClick={() => handleRunModeChange(step.id, 'specific')}
                            disabled={!specificEnabled}
                            className={`btn-toggle ${runMode === 'specific' ? 'active' : ''}`}
                            style={{ borderRight: '1px solid var(--border)' }}
                          >
                            Specific Payload
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRunModeChange(step.id, 'testData')}
                            disabled={!testDataEnabled}
                            className={`btn-toggle ${runMode === 'testData' ? 'active' : ''}`}
                          >
                            Test Data File
                          </button>
                        </div>
                        {!specificEnabled && (
                          <small style={{ display: 'block', color: '#999', marginTop: '6px' }}>
                            Specific payload is not configured.
                          </small>
                        )}
                        {!testDataEnabled && (
                          <small style={{ display: 'block', color: '#999', marginTop: '4px' }}>
                            Test data file is not assigned.
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {stepToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Test Step</h2>
              <button className="modal-close" onClick={closeDeleteModal} disabled={deletingStep}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this test step?</p>
              <div className="file-pill">
                <strong>{stepToDelete.name}</strong>
              </div>
              <p style={{ marginTop: '10px', color: '#d6336c' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={closeDeleteModal} disabled={deletingStep}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingStep}>
                {deletingStep ? 'Deleting...' : 'Delete Test Step'}
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 style={{ marginTop: '34px' }}>Test Groups ({filteredTestGroups.length})</h2>
      <button
        className="btn btn-primary"
        onClick={() => {
          if (showGroupForm) {
            resetGroupForm();
          } else {
            setShowGroupForm(true);
          }
        }}
      >
        {showGroupForm ? 'Cancel' : '+ Create Test Group'}
      </button>

      {showGroupForm && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>{editingGroupId ? 'Edit Test Group' : 'Create Test Group'}</h2>
          <form onSubmit={handleGroupSubmit}>
            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Login Flow Group"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={groupFormData.description}
                onChange={(e) => setGroupFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Short description for this group"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Select Existing Test Steps *</label>
              {testSteps.length === 0 ? (
                <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '6px', color: '#856404' }}>
                  No test steps available. Create test steps first.
                </div>
              ) : (
                <div
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    maxHeight: '240px',
                    overflowY: 'auto',
                    backgroundColor: 'var(--bg-input)'
                  }}
                >
                  {testSteps.map((step) => {
                    const checked = groupFormData.testStepIds.includes(step.id);
                    return (
                      <label
                        key={step.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '18px minmax(0, 1fr) auto',
                          gap: '10px',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleGroupStepToggle(step.id)}
                          style={{
                            width: '16px',
                            height: '16px',
                            margin: 0,
                            accentColor: 'var(--primary)',
                            cursor: 'pointer'
                          }}
                        />
                        <span
                          style={{
                            fontWeight: 600,
                            color: 'var(--text-main)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {step.name}
                        </span>
                        <small style={{ color: 'var(--text-muted)', justifySelf: 'end' }}>
                          {getApiName(step.apiId)}
                        </small>
                      </label>
                    );
                  })}
                </div>
              )}
              <small style={{ display: 'block', marginTop: '6px' }}>
                Selected: {groupFormData.testStepIds.length} step(s)
              </small>
            </div>

            <button type="submit" className="btn btn-success" disabled={testSteps.length === 0}>
              {editingGroupId ? 'Update Test Group' : 'Create Test Group'}
            </button>
          </form>
        </div>
      )}

      {testGroups.length === 0 ? (
        <div className="card">
          <p>No test groups created yet. Create a group from existing test steps for suite reuse.</p>
        </div>
      ) : filteredTestGroups.length === 0 ? (
        <div className="card">
          <p>No test groups found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(180px, 1fr) minmax(340px, 1.2fr) minmax(170px, 0.8fr) 140px',
                minWidth: '850px',
                gap: '10px',
                alignItems: 'center',
                padding: '0 12px',
                fontSize: '11px',
                color: '#6b7280',
                fontWeight: 700,
                textTransform: 'uppercase'
              }}
            >
              <span>Name</span>
              <span>Steps</span>
              <span>Time</span>
              <span>Actions</span>
            </div>
          </div>

          {filteredTestGroups.map((group) => {
            const stepNames = getGroupStepNames(group);
            const stepPreview = stepNames.slice(0, 3).join(', ');
            const remainingCount = Math.max(stepNames.length - 3, 0);
            const groupCanRun = canRunGroup(group);

            return (
              <div key={group.id} className="card" style={{ borderLeft: '4px solid #7c3aed', padding: '12px 14px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(180px, 1fr) minmax(340px, 1.2fr) minmax(170px, 0.8fr) 140px',
                      minWidth: '850px',
                      gap: '10px',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{group.name}</strong>
                      {group.description && (
                        <small style={{ display: 'block', color: '#64748b' }}>{group.description}</small>
                      )}
                    </div>

                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }}>
                      {stepNames.length === 0 ? 'No steps' : stepPreview}
                      {remainingCount > 0 ? ` +${remainingCount} more` : ''}
                    </div>

                    <span style={{ color: '#888', fontSize: '12px' }}>
                      {group.createdAt ? new Date(group.createdAt).toLocaleString() : '-'}
                    </span>

                    <div className="action-buttons" style={{ gap: '8px', flexWrap: 'nowrap' }}>
                      <button
                        className="btn btn-success btn-icon-sq"
                        onClick={() => handleRunGroup(group)}
                        disabled={executingGroupId === group.id || !groupCanRun}
                        title={executingGroupId === group.id ? 'Running' : (groupCanRun ? 'Run' : 'Not runnable')}
                        aria-label={executingGroupId === group.id ? 'Running' : 'Run'}
                        style={{ fontSize: '15px' }}
                      >
                        {executingGroupId === group.id ? String.fromCharCode(8230) : String.fromCharCode(9654)}
                      </button>
                      <button
                        className="btn btn-primary btn-icon-sq"
                        onClick={() => handleEditGroup(group)}
                        title="Edit"
                        aria-label="Edit"
                        style={{ fontSize: '15px' }}
                      >
                        {String.fromCharCode(9998)}
                      </button>
                      <button
                        className="btn btn-danger btn-icon-sq"
                        onClick={() => openDeleteGroupModal(group)}
                        title="Delete"
                        aria-label="Delete"
                        style={{ fontSize: '15px' }}
                      >
                        {String.fromCodePoint(128465)}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {groupToDelete && (
        <div className="modal-overlay" onClick={closeDeleteGroupModal}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Test Group</h2>
              <button className="modal-close" onClick={closeDeleteGroupModal} disabled={deletingGroup}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this test group?</p>
              <div className="file-pill">
                <strong>{groupToDelete.name}</strong>
              </div>
              <p style={{ marginTop: '10px', color: '#d6336c' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={closeDeleteGroupModal} disabled={deletingGroup}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDeleteGroup} disabled={deletingGroup}>
                {deletingGroup ? 'Deleting...' : 'Delete Test Group'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default TestSteps;
